import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function KeyPersonInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation - all logic handled by the component */}
          <ServicePageNavigation currentSlug="key-person-insurance" />
          
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Key Person Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Protecting Your Business Against the Loss of Indispensable Talent
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Key Person Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Key Person Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Key Person Insurance (also known as key man insurance) is a type of life insurance policy that a business purchases on the life of an owner, top executive, or another individual considered critical to the business's success. The business is the beneficiary of the policy and pays the premiums.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                If that key person dies unexpectedly, the policy pays out a death benefit to the business. These funds provide crucial financial support during a challenging transition period, helping the business manage the potential operational and financial disruption caused by the loss.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How Key Person Insurance Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Identify Key Personnel:</strong> The business identifies employees whose absence would significantly impact profitability, operations, or strategic direction. This could include founders, CEOs, top salespeople, lead engineers, etc.
                </li>
                <li>
                  <strong>Determine Coverage Amount:</strong> The business calculates the potential financial loss associated with the key person's death (e.g., lost revenue, recruitment costs, debt repayment needs) to determine an appropriate coverage amount.
                </li>
                <li>
                  <strong>Purchase Policy:</strong> The business applies for and purchases a life insurance policy (often Term or Permanent) on the key individual, naming itself as the beneficiary. The key person typically needs to consent and undergo underwriting.
                </li>
                <li>
                  <strong>Pay Premiums:</strong> The business pays the ongoing premiums for the policy.
                </li>
                <li>
                  <strong>Receive Payout (if needed):</strong> If the insured key person passes away while the policy is active, the insurance company pays the tax-free death benefit directly to the business.
                </li>
                <li>
                  <strong>Utilize Funds:</strong> The business uses the funds to cover losses, recruit a replacement, pay off debts, reassure lenders and investors, distribute funds to owners, or facilitate an orderly wind-down or sale if necessary.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits for Your Business</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Business Continuity:</strong> Provides immediate funds to manage operations, cover short-term losses, and maintain stability during the transition after losing a vital team member.</li>
                <li><strong>Financial Stability:</strong> Helps cover debts, satisfy lenders, and reassure investors or partners about the business's ongoing viability.</li>
                <li><strong>Recruitment & Training:</strong> Funds the expensive process of finding, hiring, and training a qualified replacement for the key person.</li>
                <li><strong>Buy-Sell Agreement Funding:</strong> Can provide funds needed to buy out the deceased key person's shares or ownership interest from their estate, as stipulated in a buy-sell agreement.</li>
                <li><strong>Peace of Mind:</strong> Offers assurance to owners, employees, and stakeholders that the business has a plan to weather the loss of essential personnel.</li>
                <li><strong>Tax-Free Benefit:</strong> The death benefit received by the business is generally tax-free.</li>
              </ul>
            </Card>

            {/* Risks & Considerations (General Placeholder) */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">While valuable, consider the following:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Premium Costs:</strong> Ongoing premium payments are an expense for the business (though generally not tax-deductible).</li>
                <li><strong>Underwriting:</strong> The key person must qualify medically and financially for the insurance.</li>
                <li><strong>Coverage Adequacy:</strong> Regularly review coverage amounts to ensure they remain sufficient as the business grows and personnel value changes.</li>
                <li><strong>Policy Type Selection:</strong> Choosing between Term and Permanent insurance depends on the duration of the need and budget.</li>
                <li><strong>Employee Departure:</strong> Need a plan for what happens to the policy if the key employee leaves the company before passing away (e.g., transfer ownership, surrender policy).</li>
              </ul>
            </Card>

            {/* Suitability (General Placeholder) */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Key Person Insurance Right for Your Business?</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">This strategy is crucial for businesses, particularly small to medium-sized enterprises, that:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Heavily rely on one or a few individuals for revenue generation, strategic direction, or essential operations.</li>
                <li>Have significant debt or financial obligations that could be jeopardized by the loss of a key leader.</li>
                <li>Need to fund buy-sell agreements among partners or shareholders.</li>
                <li>Want to ensure stability and confidence for lenders, investors, and employees.</li>
                <li>Operate in industries where specialized knowledge or relationships held by key individuals are critical.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Protect Your Business's Future</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Don't let the unexpected loss of a key individual derail your business. Let's assess your key person risk and explore how insurance can provide vital protection.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Key Person Protection
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
         <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}